import { Box, Card, Grid2 as Grid, Stack, SxProps, Theme, Typography } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import { mergeSx } from 'framework/utils';

export interface CreateEditFormField {
  id: string;
  content: React.ReactNode;
  sx?: SxProps<Theme>;
  size?: {
    xs?: number;
    md?: number;
    xl?: number;
  };
}

export interface CreateEditFormSection {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  fields?: CreateEditFormField[];
  content?: React.ReactNode;
}

export interface CreateEditFormContentLayoutProps {
  header?: string;
  sections: CreateEditFormSection[];
  submitButton: {
    title?: string;
    onSubmit: () => void;
    align?: 'left' | 'right';
    disabled?: boolean;
  };
  secondaryButton?: {
    title?: string;
    onClick: () => void;
    disabled?: boolean;
  };
  sx?: SxProps<Theme>;
}

const DEFAULT_FIELD_SIZE = { xs: 12, md: 4, xl: 4 };

const cardSx: SxProps<Theme> = {
  p: 2,
};

const sectionHeaderSx: SxProps<Theme> = {
  pb: 0.5,
  borderBottom: 1,
  borderColor: 'primary.light',
};

const CreateEditFormContentLayout: React.FC<CreateEditFormContentLayoutProps> = ({
  header,
  sections,
  submitButton,
  secondaryButton,
  sx,
}) => {
  const submitButtonAlignment = submitButton.align ?? 'right';

  return (
    <Card sx={mergeSx(cardSx, sx)}>
      <Stack gap={2}>
        {header && (
          <Typography variant="subtitle1" color="text.secondary">
            {header}
          </Typography>
        )}
        {sections.map(section => (
          <Stack key={section.id} gap={1.5}>
            {(section.title || section.description || section.actions) && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={2}
                sx={sectionHeaderSx}
              >
                <Box>
                  {section.title && (
                    <Typography variant="subtitle1" color="brand">
                      {section.title}
                    </Typography>
                  )}
                  {section.description && (
                    <Typography variant="body2" color="text.secondary">
                      {section.description}
                    </Typography>
                  )}
                </Box>
                {section.actions && <Box>{section.actions}</Box>}
              </Stack>
            )}
            {section.fields && section.fields.length > 0 && (
              <Grid container spacing={2}>
                {section.fields.map(field => (
                  <Grid
                    key={field.id}
                    size={{
                      xs: field.size?.xs ?? DEFAULT_FIELD_SIZE.xs,
                      md: field.size?.md ?? DEFAULT_FIELD_SIZE.md,
                      xl: field.size?.xl ?? DEFAULT_FIELD_SIZE.xl,
                    }}
                    sx={field.sx}
                  >
                    {field.content}
                  </Grid>
                ))}
              </Grid>
            )}
            {section.content}
          </Stack>
        ))}
        <Stack direction="row" justifyContent={submitButtonAlignment === 'left' ? 'flex-start' : 'flex-end'} gap={1}>
          <Button variant="contained" onClick={submitButton.onSubmit} disabled={submitButton.disabled}>
            {submitButton.title ?? 'Submit'}
          </Button>
          {secondaryButton && (
            <Button variant="outlined" onClick={secondaryButton.onClick} disabled={secondaryButton.disabled}>
              {secondaryButton.title ?? 'Save'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default CreateEditFormContentLayout;
